/** The generated hooks with their envelopes unwrapped (`apiFetch` throws on every error). */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { productColours } from "../palette";
import { ApiError } from "./fetcher";
import {
  blocks,
  getBlocksQueryKey,
  useEvents,
  useMe,
  usePiles,
  useSite,
  useStatus,
  useVersion,
} from "./generated/stockvol";
import type { BlocksOut, Me, PageEventOut, PileOut, SiteOut, StatusOut } from "./generated/model";

export const STATUS_POLL_MS = 15_000;

type Envelope<T> = { data: T; status: number };
const data = <T>(r: unknown): T => (r as Envelope<T>).data;

export function useMeInfo() {
  return useMe({ query: { select: data<Me>, retry: false, staleTime: 60_000 } });
}

export function useSiteInfo(enabled = true) {
  return useSite({ query: { select: data<SiteOut>, staleTime: 5 * 60_000, enabled } });
}

export function useStatusPoll(enabled = true) {
  return useStatus({ query: { select: data<StatusOut>, refetchInterval: STATUS_POLL_MS, enabled } });
}

export function useOpenEvents(enabled = true) {
  return useEvents(
    { open_only: true, limit: 100 },
    { query: { select: data<PageEventOut>, refetchInterval: STATUS_POLL_MS, enabled } },
  );
}

export interface VersionInfo {
  version?: string;
  algorithm_version?: string;
  git_sha?: string;
}

/** The build that runs (`/api/version`, public): shown in the sidebar and on the login page. */
export function useVersionInfo() {
  return useVersion({ query: { select: data<VersionInfo>, retry: false, staleTime: Infinity } });
}

/** The site's piles (`/api/piles`) — their products, for colours and legends. */
export function usePileList(enabled = true) {
  return usePiles(undefined, { query: { select: data<PileOut[]>, staleTime: 5 * 60_000, enabled } });
}

/**
 * Colour of a product, the same on the plan view, the pile table and the trends (stable while the
 * site's set of products is). Products of piles passed in are added to the site's.
 */
export function useProductColours(extra: readonly (string | null | undefined)[] = []) {
  const piles = usePileList();
  const key = [...(piles.data ?? []).map((p) => p.product), ...extra].join("|");
  return useMemo(() => productColours([...(piles.data ?? []).map((p) => p.product), ...extra]), [key]);
}

/** Code → product of every pile of the site. */
export function usePileProducts() {
  const piles = usePileList();
  return useMemo(() => new Map((piles.data ?? []).map((p) => [p.code, p.product] as const)), [piles.data]);
}

/**
 * A run's coal by pile with its lab values (`/api/runs/{id}/blocks`, M7.9); null when the run has
 * no block model (404), so pages show nothing rather than an error.
 */
export function useCoalQuality(runId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: getBlocksQueryKey(runId ?? ""),
    queryFn: async () => {
      try {
        return data<BlocksOut>(await blocks(runId as string));
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: enabled && runId !== undefined && runId !== "",
    staleTime: 30_000,
  });
}
